import { CrudBaseComponent } from '$core/base/_crud.service';
import { Basket } from '$core/models/subscription-order';
import { AuthenticationService } from '$core/services/auth.service';
import { ConfigService } from '$core/services/config.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { SwalErrorService } from '$shared/components/swal-error/swal-error.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

interface TotalData {
  total: number;
  total_include_nds: number;
  count: number;
}

class MockItems {
  public data: Basket[];

  constructor(items: Basket[] = []) {
    items = items.map((el) => new Basket(el));
    this.data = items;
  }
}

@Injectable({ providedIn: 'root' })
export class CartService extends CrudBaseComponent {
  public forceUpdate$ = new BehaviorSubject(null);
  public MODEL = Basket;
  public apiRoute = '/basket';
  public isLoading$ = new BehaviorSubject(false);

  public payMode = false;
  public smallWidget: boolean;
  public showWidget: boolean;

  private _total: TotalData = {
    total: 0,
    total_include_nds: 0,
    count: 0,
  };
  private _fullModeRotes = [this.configService.urlByService('subscription')];
  private _hideModeRoutes = ['/cart'];
  private activeRoute: string;

  public get total(): TotalData {
    return this._total;
  }

  public set total(t) {
    this._total = t;
  }

  constructor(
    private router: Router,
    private configService: ConfigService,
    private translateService: TranslateService,
    private swalService: SwalService,
    private auth: AuthenticationService,
    private swalErrorService: SwalErrorService,
    protected http: HttpClient
  ) {
    super(http);
    this.router.events
      .pipe(
        filter(
          (event: Event): event is NavigationEnd =>
            event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        this.activeRoute = event.url;
        this.checkShowWidget();
        this.checkSmallWidget();
      });
    this.activeRoute = this.router.url;
    this.checkShowWidget();
    this.checkSmallWidget();

    this.auth.isAuthorized$.subscribe((el) => {
      this.total = {
        total: 0,
        total_include_nds: 0,
        count: 0,
      };
      if (el) {
        this.startServerCart();
      } else {
        this.startLocalCart();
      }
    });
  }

  public addItem(items: Basket[], orderData?) {
    if (this.auth.authorizedSnapshot) {
      this._setItems(items, undefined, orderData);
    } else {
      const newItems = [];

      items.forEach((it) => {
        if (it.delivery_type !== 'virtual' || !this.localItems) {
          newItems.push(it);
        } else {
          let haveNoInLocal = false;
          this.localItems.forEach((localIt) => {
            if (
              localIt.delivery_type === 'virtual' &&
              // tslint:disable-next-line:triple-equals
              localIt.index == it.index &&
              JSON.stringify(localIt.months) === JSON.stringify(it.months)
            ) {
              haveNoInLocal = true;
            }
          });
          if (!haveNoInLocal) {
            newItems.push(it);
          }
        }
      });

      this.localItems = [...newItems, ...(this.localItems || [])];
      const swal = this.swalService.swal;
      swal.icon = 'success';
      swal.title = this.translateService.instant('SUCCESSFULLY_ADDED_TO_CARD');
      swal.text = '';
      swal.fire();
    }
  }

  public calcTotal() {
    if (this.auth.authorizedSnapshot) {
      this._getTotals();
    } else {
      this.calcLocalPrice(this.localItems);
    }
  }
  public clearLocalItems() {
    this.localItems = null;
  }
  public updateItem(item, items?) {
    // TODO:
    if (this.auth.authorizedSnapshot) {
      this._updateItem(item);
    } else {
      this.localItems = items;
      this.calcLocalPrice(items);
    }
  }

  public getItems(): Promise<Basket[]> {
    if (this.auth.authorizedSnapshot) {
      return this._getItems();
    } else {
      return new Promise((resolve) => {
        resolve(new MockItems(this.localItems).data);
      });
    }
  }

  public deleteItem(ids, local_ids?): Promise<any> {
    if (this.auth.authorizedSnapshot) {
      return this.http
        .request('delete', `${this.apiRouteWithVersion}`, { body: { ids } })
        .toPromise();
    } else {
      return new Promise((resolve) => {
        this.localItems = this.localItems.filter((el) => {
          return local_ids.indexOf(el.local_id) === -1;
        });
        resolve({ status: 'ok' });
      });
    }
  }

  public nextPage(url): Promise<Basket[]> {
    return this.http
      .request<Basket[]>(this.apiRouteWithVersion, url)
      .toPromise();
  }

  public onError(error) {
    if (error.status === 422) {
      this.swalErrorService.showError.next({
        title: 'AN_ERROR_HAS_OCCURRED',
        text: this.swalErrorService.parseError(error.error),
      });
    } else {
      const res: HttpErrorResponse = error;
      const swal = this.swalService.swal;
      swal.icon = 'error';
      swal.title = this.translateService.instant('AN_ERROR_HAS_OCCURRED');
      swal.text = res.error.message || res.statusText;
      swal.fire();
    }
  }

  public pay() {
    return this.http
      .post<any>('/v1/orders/internet-subscription', {
        payment_method: 'webpay',
      })
      .toPromise();
  }

  public clear() {
    return this.http.delete<any>('/v1/basket').toPromise();
  }

  private calcLocalPrice(items: Basket[], nds?): number {
    let tp = 0;
    const type = nds ? 'include_nds' : 'price';
    if (items) {
      items.forEach((el) => {
        tp += el[type] || 0;
      });
    }
    return tp;
  }

  private async _getItems() {
    return this.http
      .get<Basket[]>(this.apiRouteWithVersion)
      .pipe(
        map((res) => {
          res = res.map((el) => new this.MODEL(el));
          return res;
        })
      )
      .toPromise();
  }

  public async _getTotals() {
    try {
      this.isLoading$.next(true);
      this.total = (await this.http
        .get<TotalData>(`${this.apiRouteWithVersion}/total`)
        .toPromise()) ?? {
        total: 0,
        total_include_nds: 0,
        count: 0,
      };
    } catch (error) {
      this.onError(error);
    } finally {
      this.isLoading$.next(false);
    }
  }

  private async _setItems(
    items,
    onLogin?,
    orderData?: { order_id: number; status?: String }
  ) {
    try {
      await this.http
        .post<Basket[]>(this.apiRouteWithVersion, { data: items })
        .toPromise();
      this._getTotals();
      if (onLogin) {
        this.forceUpdate$.next(true);
      } else {
        const swal = this.swalService.swal;
        swal.icon = 'success';
        swal.title = this.translateService.instant(
          'SUCCESSFULLY_ADDED_TO_CARD'
        );

        if (orderData) {
          swal.showCancelButton = true;
          swal.cancelButtonText =
            this.translateService.instant('BACK_TO_ORDER');
        }

        swal.text = '';

        if (orderData) {
          swal.fire().then((result) => {
            if (result.dismiss) {
              this.router.navigate(['/personal', 'orders'], {
                queryParams: orderData,
              });
            }
            swal.showCancelButton = false;
          });
        } else {
          swal.fire();
        }
      }
    } catch (error) {
      console.log(error);

      this.onError(error);
      this._getTotals();
      this.forceUpdate$.next(true);
    } finally {
      this.localItems = null;
    }
  }

  private async _deleteItem(id) {
    try {
      await this.http
        .delete<Basket[]>(`${this.apiRouteWithVersion}/${id}`)
        .toPromise();
    } catch (error) {
      this.onError(error);
    } finally {
      this.localItems = null;
    }
  }

  private async _updateItem(item) {
    try {
      this.isLoading$.next(true);
      const i: any = await this.http
        .put<Basket[]>(`${this.apiRouteWithVersion}/${item.id}`, item)
        .toPromise();
      if (i.months) {
        item.months = i.months;
        item.proce = i.months;
      }
      this._getTotals();
    } catch (error) {
      this.onError(error);
    } finally {
      this.isLoading$.next(false);
    }
  }

  private startLocalCart() {
    const items = this.localItems;
    this.total = {
      total: this.calcLocalPrice(items),
      total_include_nds: this.calcLocalPrice(items, true),
      count: items ? items.length : 0,
    };
    this.forceUpdate$.next(true);
  }

  private startServerCart() {
    const localItem = this.localItems;
    this.localItems = null;
    if (localItem) {
      this._setItems(localItem, true);
    } else {
      this._getTotals();
      this.forceUpdate$.next(true);
    }
  }

  private checkSmallWidget(): void {
    this.smallWidget =
      this._fullModeRotes.filter((el) => {
        return this.activeRoute.indexOf(el) !== -1;
      }).length === 0;
  }

  private checkShowWidget(): void {
    this.showWidget =
      this._hideModeRoutes.filter((el) => {
        return this.activeRoute.indexOf(el) !== -1;
      }).length === 0;
  }

  private get localItems() {
    let items;
    try {
      const tmp = localStorage.getItem('internet-subscription-items');
      if (tmp) {
        items = JSON.parse(tmp);
      }
    } catch (error) {
      this.onError(error);
    }
    return items;
  }

  /**
   * set null or [] for remove from localstorage
   */
  private set localItems(items) {
    try {
      if (items && items.length) {
        const tmp = localStorage.setItem(
          'internet-subscription-items',
          JSON.stringify(items)
        );
        this.total = {
          count: items.length,
          total: this.calcLocalPrice(items),
          total_include_nds: this.calcLocalPrice(items, true),
        };
      } else {
        localStorage.removeItem('internet-subscription-items');
        this.total = {
          count: 0,
          total_include_nds: 0,
          total: 0,
        };
      }
    } catch (error) {
      this.onError(error);
    }
  }
}
